<template>
  <div class="document">
    <div class="header">
      <EsHeader>
        <template #header>
          Sources
        </template>
        <template #actions v-if="hasModule('SYS:SYS:CONFIGMGR:EDIT')">
          <ul>
            <li class="p-narrow no-border">
              <div class="list-content">
                <a role="button" tabindex="0"
                  @keyup.enter="editPanel()"
                  @click="editPanel()"
                  :class="{ disabled: editOtherPanels }"
                  v-if="!editCurrentPanel"
                  title="Edit"
                >
                  <font-awesome-icon :icon="['far', 'edit']" size="lg"/>
                </a>
              </div>
            </li>
          </ul>
        </template>
      </EsHeader>
    </div>
    <es-loader v-if="loading" style="margin: 5rem"/>
    <es-exception
      v-else-if="loadingError"
      exceptionMessage="There was an issue. Please try again or contact your administrator."
      margin="0"
      style="padding: 5rem"
    />
    <div v-else class="content"
      style="padding-top: 1.15rem">
      <table>
        <tbody>
          <tr>
            <th class="rule"
              title="Source Name"
              :class="{ 'no-border': editCurrentPanel }">
                Source Name
            </th>
            <th class="rule"
              title="Source Code"
              :class="{ 'no-border': editCurrentPanel }">
                Source Code
            </th>
            <th class="rule"
              title="Reliability Score"
             :class="{ 'no-border': editCurrentPanel }">
                Reliability Score
            </th>
            <th class="rule"
              title="Status"
              :class="{ 'no-border': editCurrentPanel }">
                Status
            </th>
            <th class="rule"
              title="Default"
              :class="{
                'no-border': editCurrentPanel,
                'last-child': !editCurrentPanel
                }">
                Default
            </th>
          </tr>
          <tr v-for="(source, index) in form"
            :key="index">
            <td :class="{ 'no-border': editCurrentPanel }">
              <div class="control">
                <input
                  class="input"
                  :class="{
                    'is-static': !editCurrentPanel,
                    'is-danger' : hasErrorSourceName[index]
                    }"
                  @keyup.prevent="validateField('sourceName',index)"
                  :readonly="!editCurrentPanel"
                  type="text"
                  v-model="source.description"
                  :title="source.description"
                  :aria-label="source.description"
                >
              </div>
              <span v-if="hasErrorSourceName[index]"
                class="help is-danger">
                {{ msgSourceName[index] }}
              </span>
            </td>
            <td :class="{ 'no-border': editCurrentPanel }">
              <div class="control">
                <input
                  class="input is-static"
                  readonly
                  type="text"
                  v-model="source.code"
                  :title="source.code"
                  :aria-label="source.code"
                >
              </div>
            </td>
            <td :class="{ 'no-border': editCurrentPanel }">
              <div class="control">
                <input
                  class="input"
                  :class="{
                    'is-static': !editCurrentPanel,
                    'is-danger' : hasErrorReliabilityScore[index]
                    }"
                  @keypress="checkInput($event)"
                  @keyup.prevent="validateField('reliabilityScore',index)"
                  :readonly="!editCurrentPanel"
                  type="text"
                  v-model="source.reliability"
                  :title="source.reliability"
                  :aria-label="source.reliability"
                >
              </div>
              <span v-if="hasErrorReliabilityScore[index]"
                  class="help is-danger">
                {{ msgReliabilityScore[index] }}
              </span>
            </td>
            <td :class="{ 'no-border': editCurrentPanel }">
              <div class="control">
                <div
                  class="buttons has-addons"
                  v-if="editCurrentPanel"
                >
                  <button
                    class="button button--form"
                    :disabled="source.isDefault"
                    v-for="(option, index) in booleanOptions"
                    :key="index"
                    @click="toggle(option.code, source)"
                    :class="{'is-active': source.active === option.code}"
                  >
                    {{ option.activeInactive }}
                  </button>
                </div>
                <input
                  v-else
                  class="input"
                  :class="{ 'is-static': !editCurrentPanel }"
                  type="text"
                  :readonly="!editCurrentPanel"
                  :value="booleanOptions.find(item => item.code === source.active
                  ).activeInactive"
                  :title="booleanOptions.find(item => item.code === source.active
                  ).activeInactive"
                  :aria-label="booleanOptions.find(item => item.code === source.active
                  ).activeInactive"
                >
              </div>
            </td>
            <td
              :class="{
                'no-border': editCurrentPanel,
                'last-child': !editCurrentPanel
                }">
              <div class="control">
                <input
                  class="input is-static"
                  :class="{ 'is-readonly': editCurrentPanel }"
                  type="text"
                  readonly
                  :value="booleanOptions.find(
                    item => item.code === source.isDefault
                  ).yesNo"
                  :aria-label="booleanOptions.find(
                    item => item.code === source.isDefault
                  ).yesNo"
                >
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div
      v-if="editCurrentPanel"
      class="is-flex flex-justify--end flex-align--center"
      style="margin-right: 5.15rem; padding-bottom: 2rem"
    >
      <button
        class="button button-link has-underline"
        @click.prevent="cancelPanel()"
        style="margin-right: 1rem"
      >
        Cancel
      </button>
      <button
        class="button is-primary"
        @click.prevent="updatePanel()"
      >
        Update
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDoubleRight,
} from '@fortawesome/pro-regular-svg-icons';
import EsException from '@/components/EsException.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faAngleDoubleRight,
);
const SOURCE_NAME_REGEX = /^[a-zA-Z0-9 ._'-]*$/;
const NUMERIC_REGEX = /^\d+$/;
export default {
  mixins: [
    NotificationManager,
    PermissionManager,
  ],
  created() {
    this.onLoad();
    this.booleanOptions = [
      {
        code: true,
        yesNo: 'Yes',
        activeInactive: 'Active',
      },
      {
        code: false,
        yesNo: 'No',
        activeInactive: 'Inactive',
      },
    ];
  },
  mounted() {
    this.updateCache();
  },
  components: {
    EsException,
    EsHeader,
    EsLoader,
  },
  props: {
    editOtherPanels: {
      type: Boolean,
    },
  },
  data() {
    return this.initialData();
  },
  methods: {
    cancelPanel() {
      this.clearErrors();
      this.undoForm();
      this.hideNotification();
      this.editCurrentPanel = false;
    },
    checkForFormChanges() {
      return this.compareObjects(this.cache, this.form);
    },
    checkError(obj) {
      return Object.values(obj).filter(value => !!value).length > 0
    },
    clearErrors() {
      this.hideNotification();
      this.hasErrorSourceName = {};
      this.msgSourceName = {};
      this.hasErrorReliabilityScore = {};
      this.msgReliabilityScore = {};
    },
    compareObjects(o, n) {
      return JSON.stringify(o) !== JSON.stringify(n);
    },
    checkInput(evt) {
      // Listen to the key event when a keyboard key is pressed
      evt = (evt) || window.event; // eslint-disable-line
      // Store the ASCII value of the pressed key in 'charCode'
      const charCode = (evt.which) ? evt.which : evt.keyCode;
      // Prevent user from entering any values except digits
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        evt.preventDefault();
      }
    },
    editPanel() {
      this.hideNotification();
      this.editCurrentPanel = !this.editCurrentPanel;
    },
    initialData() {
      return {
        cache: {},
        editCurrentPanel: false,
        loading: false,
        loadingError: false,
        form: {},
        hasErrorSourceName: {},
        msgSourceName: {},
        hasErrorReliabilityScore: {},
        msgReliabilityScore: {},
      };
    },
    async onLoad() {
      this.loading = true;
      this.loadingError = false;
      try {
        const res = await axios.get('sources');
        this.form = res.data.data;
        this.updateCache();
      } catch (err) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    toggle(code, source) {
      source.active = code;
      if (source.isDefault === true) {
        source.active = true;
      }
    },
    updatePanel() {
      Object.keys(this.form).forEach((key) => {
        this.validateField('sourceName', key);
      });
      const err = this.checkError(this.hasErrorSourceName)
      || this.checkError(this.hasErrorReliabilityScore)
      if (err) {
        this.showNotification('danger', ['There were some errors. Please fix the errors highlighted in the fields below.']);
      }
      else {
        this.hideNotification();
        this.updateSource();
      }
    },
    undoForm() {
      this.form = JSON.parse(JSON.stringify(this.cache));
    },
    updateCache() {
      this.cache = JSON.parse(JSON.stringify(this.form));
    },
    async updateSource() {
      // Return without firing API if there are no changes on the page from the User
      const changes = this.checkForFormChanges();
      if (!changes) {
        this.editCurrentPanel = false;
        return true;
      }
      try {
        await axios({
          method: 'put',
          url: '/sources',
          data: this.form,
        });
        await this.onLoad();
        this.updateCache();
        this.showNotification('success', ['The Sources have been updated successfully.']);
        this.editCurrentPanel = false;
      } catch (error) {
        let apiMsg = 'There were some errors.';
        const results = error.response && error.response.data;
        if (results) {
          apiMsg += results.map(r => r.error).join(', ');
        }
        this.showNotification('danger', [apiMsg]);
      }
    },

   validateField(field, index) {
      if (field === 'sourceName') {
        const value = this.form[index].description;
        if (!value) {
          this.$set(this.hasErrorSourceName, index, true);
          this.$set(this.msgSourceName, index, 'Source Name is required.');
        } else if (value.length > 150) {
          this.$set(this.hasErrorSourceName, index, true);
          this.$set(this.msgSourceName, index, 'Source Name allows a maximum of 150 characters.');
        } else if (!SOURCE_NAME_REGEX.test(value)) {
          this.$set(this.hasErrorSourceName, index, true);
          this.$set(this.msgSourceName, index, 'Source Name cannot include special characters.');
        } else {
          this.$set(this.hasErrorSourceName, index, false);
          this.$set(this.msgSourceName, index, null);
        }
      }
      if (field === 'reliabilityScore') {
        const reliability = this.form[index].reliability;
        const reliabilityNum = Number(reliability);
        if (!reliability) {
          this.$set(this.hasErrorReliabilityScore, index, true);
          this.$set(this.msgReliabilityScore, index, 'Reliability Score is required.');
        } else if (!NUMERIC_REGEX.test(reliability)) {
          this.$set(this.hasErrorReliabilityScore, index, true);
          this.$set(this.msgReliabilityScore, index, 'Reliability Score must be numeric.');
        } else if (reliabilityNum < 0 || reliabilityNum > 100) {
          this.$set(this.hasErrorReliabilityScore, index, true);
          this.$set(this.msgReliabilityScore, index, 'Reliability Score must be numeric between 0 and 100.');
        } else {
          this.$set(this.hasErrorReliabilityScore, index, false);
          this.$set(this.msgReliabilityScore, index, null);
        }
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  .header
    display: flex
    align-items: center
  .last-child
    font-size: 12px
    text-align: right
    width: 1%
    white-space: nowrap
    padding-right: 4rem
  .no-border
    border: 0
  .rule
    font-size: 12px
    color: #666
  .content
    overflow-x: auto
  input
    text-overflow: ellipsis
</style>
